import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarMatrix } from "@/components/CalendarMatrix";
import type { Booking } from "@/types";
import { toast } from "sonner";
import { useContracts } from "@/contexts/ContractContext";
import { useCalendar } from "@/contexts/CalendarContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { createBookings } from "@/api/bookings";
import { Button } from "@/components/ui/button";

export function Calendar() {
  const {
    unitList,
    selectedUnit,
    setSelectedUnit,
    isLoading,
    unitAvailability,
    clearCart,
    refreshAvailability,
  } = useCalendar();

  const { contracts, updateContractStatus, fetchContracts } = useContracts();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedResort, setSelectedResort] = useState<string>('');

  const availableResorts = useMemo(() => {
    const seen = new Set<string>();
    return unitList.reduce<string[]>((acc, u) => {
      if (!seen.has(u.resort)) {
        seen.add(u.resort);
        acc.push(u.resort);
      }
      return acc;
    }, []);
  }, [unitList]);

  const filteredUnits = useMemo(
    () => selectedResort ? unitList.filter(u => u.resort === selectedResort) : [],
    [unitList, selectedResort]
  );

  useEffect(() => {
    const allBookings = contracts.flatMap(contract => contract.bookings || []);
    setBookings(allBookings);
  }, [contracts]);

  // Auto-select first resort when unit list loads
  useEffect(() => {
    if (availableResorts.length > 0 && !selectedResort) {
      setSelectedResort(availableResorts[0]);
    }
  }, [availableResorts, selectedResort]);

  const handleResortChange = useCallback((resort: string) => {
    setSelectedResort(resort);
    const firstUnit = unitList.find(u => u.resort === resort);
    if (firstUnit) {
      setSelectedUnit(firstUnit.number);
    }
  }, [unitList, setSelectedUnit]);

  const handleUnitSelection = useCallback((roomNumber: string) => {
    setSelectedUnit(roomNumber);
  }, [setSelectedUnit]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshAvailability();
      toast.success('Calendar refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh calendar');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleBookWeeks = async (contractId: number, bookingsToAdd: Booking[], bookingsResponse?: any) => {
    try {
      setIsBookingLoading(true);
      updateContractStatus(contractId, 'pending');

      if (!bookingsResponse) {
        bookingsResponse = await createBookings(contractId, bookingsToAdd);
      }

      await fetchContracts();

      setBookings(prevBookings => [
        ...prevBookings,
        ...bookingsToAdd.map(booking => ({ ...booking, contractId })),
      ]);

      await refreshAvailability();
      toast.success('Bookings created successfully');
      clearCart();
    } catch (error) {
      console.error('Calendar page - Error in booking process:', error);
      toast.error('Failed to create booking');
      updateContractStatus(contractId, 'new');
    } finally {
      setIsBookingLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Calendar</h1>
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading || isBookingLoading}
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
          <Select value={selectedResort} onValueChange={handleResortChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select a resort" />
            </SelectTrigger>
            <SelectContent>
              {availableResorts.length > 0 ? availableResorts.map(resort => (
                <SelectItem key={resort} value={resort}>{resort}</SelectItem>
              )) : (
                <SelectItem value="no-resorts" disabled>No resorts available</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select
            value={selectedUnit}
            onValueChange={handleUnitSelection}
            disabled={!selectedResort || filteredUnits.length === 0}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select room" />
            </SelectTrigger>
            <SelectContent>
              {filteredUnits.map(unit => (
                <SelectItem key={unit.number} value={unit.number}>
                  {unit.number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Card>
        <CardContent className="p-6">
          {isLoading || isBookingLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-muted-foreground">
                {isBookingLoading ? 'Creating bookings...' : 'Loading units...'}
              </p>
            </div>
          ) : selectedUnit ? (
            <CalendarMatrix
              roomNumber={selectedUnit}
              unitAvailability={unitAvailability}
              contracts={contracts}
              bookings={bookings}
              onBookWeeks={handleBookWeeks}
            />
          ) : (
            <div className="text-center text-muted-foreground py-12">
              Select a unit to view its calendar
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
